/**
 * Onboarding **checklists** for one employee — WP-W6.
 * `web/shapes/hr-onboarding.md` §5 is the agreed shape.
 *
 * Unlike the templates panel (company config), a checklist is **about one employee**, so
 * this surface is keyed by an employee id. `GET /employees` (Task T2-A) and
 * `EmployeePicker` (Task T2-B) exist now, but this panel has not been rewired to use
 * them, so the id is still typed — the same pattern the documents screen and the
 * recruiting screens use. The server resolves the WHOSE gate over the caller's tree;
 * naming an id asserts no scope.
 *
 * List an employee's checklists (each with its tasks nested and a done/total progress),
 * create one from a template (the template picker is fed from the templates already
 * loaded above), and **complete** a task — which, when it was the last open one, closes
 * the checklist server-side. Nothing is optimistic: a completed task re-reads the list.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type {
  OnboardingChecklist,
  OnboardingChecklistCreateRequest,
  OnboardingTask,
  OnboardingTemplate,
} from '../../api/hr';
import {
  checklistProgress,
  ONBOARDING_CATEGORY_LABEL,
  ONBOARDING_CHECKLIST_STATUS_BADGE,
  ONBOARDING_CHECKLIST_STATUS_LABEL,
  ONBOARDING_TASK_STATUS_BADGE,
  ONBOARDING_TASK_STATUS_LABEL,
} from '../../hr/onboarding';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';

export interface OnboardingChecklistsPanelProps {
  readonly employeeId: string | null;
  readonly onLoadEmployee: (id: string) => void;
  readonly listStatus: QueryStatus;
  readonly checklists: readonly OnboardingChecklist[] | null;
  readonly listError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly templates: readonly OnboardingTemplate[] | null;
  readonly createPending: boolean;
  readonly createError: WorkforceApiError | null;
  readonly onCreate: (body: OnboardingChecklistCreateRequest) => void;
  readonly completingTaskId: string | null;
  readonly completeError: WorkforceApiError | null;
  readonly onComplete: (checklistId: string, taskId: string) => void;
  readonly locale: string;
  readonly timezone: string;
}

export function OnboardingChecklistsPanel(props: OnboardingChecklistsPanelProps): ReactNode {
  const { t } = useI18n();
  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [templateId, setTemplateId] = useState('');

  const activeTemplates = (props.templates ?? []).filter((template) => template.is_active);

  return (
    <section
      className="wp-section"
      aria-labelledby="w6o-checklists-heading"
      data-testid="w6o-checklists"
    >
      <h2 className="wp-section__title" id="w6o-checklists-heading">
        {t('hr.onboarding.checklists.title')}
      </h2>
      <p className="wp-small wp-muted">{t('hr.onboarding.checklists.intro')}</p>

      {/* ── Which employee ─────────────────────────────────────────────────── */}
      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = employeeIdInput.trim();
          if (trimmed === '') return;
          props.onLoadEmployee(trimmed);
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w6o-employee-id">
            {t('hr.employee.idLabel')}
          </label>
          <input
            className="wp-field__input"
            id="w6o-employee-id"
            data-testid="w6o-employee-id"
            type="text"
            inputMode="numeric"
            value={employeeIdInput}
            autoComplete="off"
            onChange={(event) => {
              setEmployeeIdInput(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('hr.employee.idHint')}</p>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w6o-employee-load"
            disabled={employeeIdInput.trim() === ''}
          >
            {t('hr.onboarding.checklists.load')}
          </button>
        </div>
      </form>

      {props.employeeId === null ? (
        <EmptyState
          title={t('hr.onboarding.checklists.none.title')}
          description={t('hr.onboarding.checklists.none.body')}
          testId="w6o-checklists-none"
        />
      ) : (
        <>
          {/* ── Create a checklist from a template ─────────────────────────── */}
          <div className="wp-section__subsection" data-testid="w6o-checklist-create">
            <h3 className="wp-section__title">{t('hr.onboarding.checklists.create.title')}</h3>
            <p className="wp-small wp-muted">{t('hr.onboarding.checklists.create.intro')}</p>
            <form
              className="wp-filters"
              onSubmit={(event) => {
                event.preventDefault();
                if (props.employeeId === null || props.createPending) return;
                props.onCreate({
                  employee_id: props.employeeId,
                  ...(templateId === '' ? {} : { template_id: templateId }),
                });
              }}
            >
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w6o-checklist-template">
                  {t('hr.onboarding.checklists.create.template')}
                </label>
                <select
                  className="wp-field__input"
                  id="w6o-checklist-template"
                  data-testid="w6o-checklist-template"
                  value={templateId}
                  onChange={(event) => {
                    setTemplateId(event.target.value);
                  }}
                >
                  <option value="">{t('hr.onboarding.checklists.create.noTemplate')}</option>
                  {activeTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <p className="wp-small wp-muted">
                  {t('hr.onboarding.checklists.create.templateHint')}
                </p>
              </div>
              <div className="wp-filters__actions">
                <button
                  className="wp-button wp-button--primary"
                  type="submit"
                  data-testid="w6o-checklist-submit"
                  disabled={props.createPending}
                >
                  {props.createPending
                    ? t('hr.onboarding.checklists.create.submitting')
                    : t('hr.onboarding.checklists.create.submit')}
                </button>
              </div>
            </form>
            {props.createError === null ? null : (
              <ApiErrorNote
                error={props.createError}
                subject={t('hr.onboarding.checklists.subject')}
                testId="w6o-checklist-create-error"
              />
            )}
          </div>

          {/* ── The list ────────────────────────────────────────────────────── */}
          {props.listStatus === 'loading' ? (
            <Skeleton
              rows={3}
              variant="block"
              label={t('hr.onboarding.checklists.subject')}
              testId="w6o-checklists-skeleton"
            />
          ) : null}
          {props.listStatus === 'error' && props.listError !== null ? (
            <ApiErrorNote
              error={props.listError}
              onRetry={props.onReload}
              subject={t('hr.onboarding.checklists.subject')}
              testId="w6o-checklists-error"
            />
          ) : null}

          {props.checklists === null ? null : props.checklists.length === 0 ? (
            <EmptyState
              title={t('hr.onboarding.checklists.empty.title')}
              description={t('hr.onboarding.checklists.empty.body')}
              testId="w6o-checklists-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="w6o-checklists-list">
              {props.checklists.map((checklist) => (
                <li
                  className="wp-section__subsection"
                  key={checklist.id}
                  data-testid={`w6o-checklist-${checklist.id}`}
                >
                  <ChecklistHeader
                    checklist={checklist}
                    locale={props.locale}
                    timezone={props.timezone}
                  />
                  <TaskList
                    checklist={checklist}
                    completingTaskId={props.completingTaskId}
                    onComplete={props.onComplete}
                  />
                </li>
              ))}
            </ul>
          )}
          {props.completeError === null ? null : (
            <ApiErrorNote
              error={props.completeError}
              subject={t('hr.onboarding.tasks.subject')}
              testId="w6o-task-complete-error"
            />
          )}
        </>
      )}
    </section>
  );
}

function ChecklistHeader({
  checklist,
  locale,
  timezone,
}: {
  readonly checklist: OnboardingChecklist;
  readonly locale: string;
  readonly timezone: string;
}): ReactNode {
  const { t } = useI18n();
  const badge = ONBOARDING_CHECKLIST_STATUS_BADGE[checklist.status];
  const progress = checklistProgress(checklist);
  return (
    <div className="wp-row__main">
      <p>
        <Badge
          severity={badge}
          icon={SEVERITY_ICON[badge]}
          label={t(ONBOARDING_CHECKLIST_STATUS_LABEL[checklist.status])}
          testId={`w6o-checklist-status-${checklist.id}`}
        />{' '}
        <span data-testid={`w6o-checklist-progress-${checklist.id}`}>
          {t('hr.onboarding.checklists.progress', { done: progress.done, total: progress.total })}
        </span>
      </p>
      <p className="wp-small wp-muted">
        {t('hr.onboarding.checklists.startedAt', {
          when:
            checklist.started_at === null
              ? t('hr.onboarding.checklists.notStarted')
              : formatInstant(checklist.started_at, locale, timezone),
        })}
      </p>
    </div>
  );
}

function TaskList({
  checklist,
  completingTaskId,
  onComplete,
}: {
  readonly checklist: OnboardingChecklist;
  readonly completingTaskId: string | null;
  readonly onComplete: (checklistId: string, taskId: string) => void;
}): ReactNode {
  const { t } = useI18n();
  if (checklist.tasks.length === 0) {
    return (
      <p className="wp-small wp-muted" data-testid={`w6o-checklist-notasks-${checklist.id}`}>
        {t('hr.onboarding.tasks.none')}
      </p>
    );
  }
  return (
    <ul className="wp-rows" data-testid={`w6o-tasks-${checklist.id}`}>
      {checklist.tasks.map((task) => (
        <TaskRow
          key={task.id}
          checklistId={checklist.id}
          task={task}
          completing={completingTaskId === task.id}
          onComplete={onComplete}
        />
      ))}
    </ul>
  );
}

function TaskRow({
  checklistId,
  task,
  completing,
  onComplete,
}: {
  readonly checklistId: string;
  readonly task: OnboardingTask;
  readonly completing: boolean;
  readonly onComplete: (checklistId: string, taskId: string) => void;
}): ReactNode {
  const { t } = useI18n();
  const badge = ONBOARDING_TASK_STATUS_BADGE[task.status];
  const isDone = task.status === 'done';
  return (
    <li className="wp-row" data-testid={`w6o-task-${task.id}`}>
      <div className="wp-row__main">
        <p>
          <Badge
            severity={badge}
            icon={SEVERITY_ICON[badge]}
            label={t(ONBOARDING_TASK_STATUS_LABEL[task.status])}
            testId={`w6o-task-status-${task.id}`}
          />{' '}
          <span>{task.title}</span>
        </p>
        <p className="wp-small wp-muted">
          {t('hr.onboarding.tasks.meta', {
            category:
              task.category === null
                ? t('hr.onboarding.category.none')
                : t(ONBOARDING_CATEGORY_LABEL[task.category]),
            due: task.due_date ?? t('hr.onboarding.tasks.noDue'),
          })}
        </p>
      </div>
      <div className="wp-row__figure">
        {isDone ? (
          <span className="wp-small wp-muted" data-testid={`w6o-task-done-${task.id}`}>
            {t('hr.onboarding.tasks.doneMark')}
          </span>
        ) : (
          <button
            className="wp-button"
            type="button"
            data-testid={`w6o-task-complete-${task.id}`}
            disabled={completing}
            onClick={() => {
              onComplete(checklistId, task.id);
            }}
          >
            {completing ? t('hr.onboarding.tasks.completing') : t('hr.onboarding.tasks.complete')}
          </button>
        )}
      </div>
    </li>
  );
}
